import { ChangeDetectionStrategy, Component, computed, effect, linkedSignal } from "@angular/core";
import { CalendarService } from "../../modules/home/calendar/calendar.service";
import { CalendarData } from "../../modules/home/calendar/CalendarData";
import { ModalComponent } from "../../modules/modal/modal.component";
import { ModalHeaderComponent } from "../../modules/modal/modal.header.component";
import { S25InfiniteScrollModule } from "../../modules/s25-infinite-scroll/s25.infinite.scroll.module";
import { S25LoadingInlineModule } from "../../modules/s25-loading/s25.loading.inline.module";
import { OptUtil } from "../../modules/s25-opt/s25.opt.util";
import { Item } from "../../pojo/Item";
import { S25Util } from "../../util/s25-util";
import { CalendarDayReservations } from "./calendar.day.reservations.component";

export interface CalendarDayEventModalData {
    expectedTotal: number | undefined;
    date: Date;
    objId: number;
    comptype: OptUtil.CompType;
    compsubject: Item.Subject;
    includeAdditional: boolean | undefined;
}

@Component({
    host: {
        class: "s25-ng calendarDayEventModal modal-content",
    },
    template: `
        <s25-modal-header [data]="params()"></s25-modal-header>
        <div class="modal-body">
            @if (!reservations()?.length && !needsMore() && !error()) {
                <div class="empty-state-message">No events on this day</div>
            }
            <s25-ng-calendar-day-reservations
                #resi
                [reservations]="reservations()"
                s25-infinite-scroll
                [onScroll]="tryRequestingMore"
                [hasMorePages]="hasMore"
                [topSelector]="'self'"
                [scrollThreshold]="0"
            >
                @if (needsMore()) {
                    @if (error()) {
                        <span class="error-text ngRed ngBold">Failed to retrieve data</span>
                    } @else {
                        <s25-ng-loading-inline-static class="fetching-more-reservations" [text]="'Fetching...'" />
                    }
                }
            </s25-ng-calendar-day-reservations>
        </div>
        <div class="modal-footer">
            &nbsp;<button
                href="javascript:void(0);"
                class="aw-button aw-button--outline"
                (click)="params()?.closeModal()"
            >
                Close
            </button>
        </div>
    `,
    styles: `
        .modal-body {
            max-height: inherit !important;
            overflow-y: inherit !important;
        }

        s25-ng-calendar-day-reservations {
            /* You might be tempted to use 85vh as we do elsewhere, but 100vh-215px accounts for the header and footer of the modal */
            max-height: calc(min(100vh, 100 * var(--embedded-vh, 1vh) - 215px));
            overflow-y: auto;
        }

        .fetching-more-reservations {
            display: flex;
            width: 100%;
            padding: 1em;
            justify-content: center;
        }

        .empty-state-message {
            padding: 2em;
            text-align: center;
            color: #666;
        }

        /* no footer border bc it intersects event and scroll bar */
        .modal-footer {
            border: none !important;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [ModalHeaderComponent, CalendarDayReservations, S25InfiniteScrollModule, S25LoadingInlineModule],
})
export class CalendarDayModal extends ModalComponent<CalendarDayEventModalData> {
    readonly hasMore = computed(() => (this.params()?.expectedTotal ?? 0) > (this.reservations()?.length ?? 0));

    readonly requestParams = computed(() => {
        if (this.params()) {
            return {
                objId: "" + this.params()!.objId,
                date: this.params()!.date,
                comptype: this.params()!.comptype,
                compsubject: this.params()!.compsubject,
                includeAdditional: this.params()!.includeAdditional ? "T" : undefined,
            };
        }
    });

    readonly numPagesDesired = linkedSignal({
        // when params change, reset to 0 desired pages
        source: this.requestParams,
        computation: () => 0,
    });
    readonly responses = linkedSignal((): (CalendarData | Promise<CalendarData>)[] | undefined => {
        if (this.requestParams()) {
            return [];
        } else {
            return undefined;
        }
    });
    readonly error = linkedSignal({
        source: this.requestParams,
        computation: () => undefined as string | undefined,
    });
    readonly numPagesFetched = computed(
        () => this.responses()?.filter((response) => !(response instanceof Promise)).length ?? 0,
    );
    readonly reservations = computed(() =>
        this.responses()?.flatMap((response) =>
            response instanceof Promise ? [] : response.events.flatMap((e) => e.rsrv ?? []),
        ),
    );
    readonly needsMore = computed(() => this.numPagesDesired() > this.numPagesFetched());

    constructor() {
        super();
        effect(() => {
            if (this.responses()?.length === 0) {
                void this.tryRequestingMore();
            }
        });
    }

    public tryRequestingMore = async () => {
        if (this.hasMore() && this.numPagesFetched() === this.numPagesDesired()) {
            this.numPagesDesired.update((p) => p + 1);
            const nextPageIndex = this.numPagesDesired() - 1;
            const pageRequest = this.getPage(nextPageIndex);
            if (!pageRequest) {
                console.error("Unexpected missing page request");
                return Promise.resolve();
            }
            this.responses.update((r) => [...(r ?? []), pageRequest]);
            let result;
            try {
                result = await pageRequest;
                this.error.set(undefined);
            } catch (e) {
                result = undefined;
                this.error.set(S25Util.errorText(e));
            }
            this.responses.update((responses) => {
                if (responses?.[nextPageIndex] !== pageRequest) {
                    // the responses have been reset, and this promise was orphaned
                    return responses;
                } else {
                    const updatedResponses = responses.slice();
                    if (result) {
                        updatedResponses[nextPageIndex] = result;
                    } else {
                        updatedResponses.splice(nextPageIndex, 1);
                    }
                    return updatedResponses;
                }
            });
        } else {
            return Promise.resolve();
        }
    };

    public async getPage(pageIndex: number): Promise<CalendarData> {
        let lastId = -1;
        if (pageIndex > 0) {
            const previousResponse = this.responses()?.[pageIndex - 1];
            if (!previousResponse) {
                throw new Error("Previous response is missing");
            } else if (previousResponse instanceof Promise) {
                lastId = (await previousResponse).last_id!;
            } else {
                lastId = previousResponse.last_id!;
            }
        }
        // TODO: cancel pending promises if the modal closes, params change, etc
        const result = await CalendarService.getCalendarDayPage({
            objId: this.requestParams()!.objId,
            date: this.requestParams()!.date,
            comptype: this.requestParams()!.comptype,
            compsubject: this.requestParams()!.compsubject,
            lastId: "" + lastId,
            includeAddtl: this.requestParams()!.includeAdditional,
        });
        return result.root;
    }
}
